// Curated post-op (post-treatment) care instructions shown on the patient
// welcome display. Static templates — one entry per treatment type. Ported from
// the "Post-Op Care Screen" Claude Design project; เคลือบฟลูออไรด์ added per
// request.
//
// Bilingual: each entry has a stable `key`, plus `label`, `dos`, `donts`, and
// `notes` as `{ th, en }` pairs (the three lists are arrays per language). The
// display resolves the active language; `dos`/`donts`/`notes` th and en arrays
// are parallel (same length, same item translated). Rendered as
// ควรปฏิบัติ / ควรหลีกเลี่ยง / ข้อควรรู้ (Do / Avoid / Good to know).

export const POSTOP_TREATMENTS = [
  {
    key: "extraction",
    label: { th: "ถอนฟัน", en: "Tooth Extraction" },
    dos: {
      th: [
        "กัดผ้าก๊อซแน่นๆ 30–60 นาที",
        "ประคบเย็นบริเวณแก้มในวันแรก",
        "ทานยาตามที่ทันตแพทย์สั่ง",
        "ทานอาหารอ่อนและเคี้ยวด้านตรงข้าม",
      ],
      en: [
        "Bite firmly on the gauze for 30–60 minutes",
        "Apply a cold compress to your cheek on the first day",
        "Take medication as prescribed by your dentist",
        "Eat soft food and chew on the opposite side",
      ],
    },
    donts: {
      th: [
        "บ้วนปากแรงหรือดูดน้ำลายใน 24 ชม.",
        "ดูดน้ำด้วยหลอด",
        "สูบบุหรี่และดื่มแอลกอฮอล์",
        "อาหารร้อนจัดหรือเผ็ดจัด",
      ],
      en: [
        "Rinsing hard or sucking/spitting within 24 hours",
        "Drinking through a straw",
        "Smoking and drinking alcohol",
        "Very hot or very spicy food",
      ],
    },
    notes: {
      th: [
        "เลือดซึมเล็กน้อยในวันแรกถือเป็นปกติ",
        "หากเลือดไหลไม่หยุดหรือบวมมากผิดปกติ ให้ติดต่อคลินิก",
        "มาตัดไหมตามนัด (ถ้ามี)",
      ],
      en: [
        "A little oozing of blood on the first day is normal",
        "If bleeding won't stop or swelling is unusually severe, contact the clinic",
        "Return to have stitches removed as scheduled (if any)",
      ],
    },
  },
  {
    key: "surgery",
    label: { th: "ผ่าฟันคุด / ผ่าตัดรากเทียม", en: "Wisdom Tooth / Implant Surgery" },
    dos: {
      th: [
        "กัดผ้าก๊อซแน่นๆ ประมาณ 1 ชม. และประคบเย็น 24 ชม.แรก",
        "วันถัดไปประคบอุ่นเพื่อช่วยลดบวม",
        "ทานยาแก้อักเสบและยาแก้ปวดให้ครบตามสั่ง",
        "พักผ่อนให้เพียงพอและหนุนหมอนสูงเวลานอน",
      ],
      en: [
        "Bite firmly on the gauze for about 1 hour and use a cold compress for the first 24 hours",
        "The next day, apply a warm compress to help reduce swelling",
        "Finish your anti-inflammatory and pain medication as prescribed",
        "Rest well and keep your head propped up on a pillow when sleeping",
      ],
    },
    donts: {
      th: [
        "บ้วนปากแรง แคะ หรือสัมผัสแผลด้วยลิ้น/นิ้ว",
        "สูบบุหรี่และดื่มแอลกอฮอล์",
        "ออกกำลังกายหนักหรือยกของหนัก",
        "อาหารแข็ง เหนียว หรือร้อนจัด",
      ],
      en: [
        "Rinsing hard, poking, or touching the wound with your tongue or fingers",
        "Smoking and drinking alcohol",
        "Strenuous exercise or heavy lifting",
        "Hard, chewy, or very hot food",
      ],
    },
    notes: {
      th: [
        "อาการบวมและอ้าปากได้น้อยใน 2–3 วันแรกถือเป็นปกติ",
        "หากปวดมากขึ้นหลังวันที่ 3 มีไข้ หรือบวมผิดปกติ ให้ติดต่อคลินิก",
        "มาตัดไหมและตรวจแผลตามนัด",
      ],
      en: [
        "Swelling and limited mouth opening in the first 2–3 days are normal",
        "If pain worsens after day 3, you develop a fever, or swelling is unusual, contact the clinic",
        "Return to have stitches removed and the wound checked as scheduled",
      ],
    },
  },
  {
    key: "scaling",
    label: { th: "ขูดหินปูน", en: "Dental Scaling" },
    dos: {
      th: [
        "แปรงฟันเบาๆ และใช้ไหมขัดฟันตามปกติ",
        "บ้วนปากด้วยน้ำเกลืออุ่นถ้ารู้สึกเสียวฟัน",
        "ดื่มน้ำมากๆ ตลอดวัน",
        "ใช้ยาสีฟันสำหรับฟันเสียว",
      ],
      en: [
        "Brush gently and floss as usual",
        "Rinse with warm salt water if your teeth feel sensitive",
        "Drink plenty of water throughout the day",
        "Use a toothpaste for sensitive teeth",
      ],
    },
    donts: {
      th: [
        "อาหารหรือเครื่องดื่มร้อน/เย็นจัดในวันแรก",
        "อาหารแข็งหรือกรอบทันทีหลังขูด",
        "สูบบุหรี่",
        "ขย้ำเหงือกบริเวณที่ระบม",
      ],
      en: [
        "Very hot or cold food and drinks on the first day",
        "Hard or crunchy food right after scaling",
        "Smoking",
        "Chewing hard on tender gum areas",
      ],
    },
    notes: {
      th: [
        "อาการเสียวฟันเล็กน้อย 1–2 วันถือเป็นปกติ",
        "เหงือกอาจระบมเล็กน้อยและจะดีขึ้นเอง",
        "นัดขูดหินปูนทุก 6 เดือน",
      ],
      en: [
        "Mild tooth sensitivity for 1–2 days is normal",
        "Gums may feel slightly tender and will recover on their own",
        "Schedule a scaling every 6 months",
      ],
    },
  },
  {
    key: "filling",
    label: { th: "อุดฟัน", en: "Dental Filling" },
    dos: {
      th: [
        "รอ 1 ชม. ก่อนเคี้ยวด้านที่อุด",
        "เคี้ยวอาหารด้านตรงข้ามในวันแรก",
        "แปรงฟันและใช้ไหมขัดฟันตามปกติ",
        "ทานยาแก้ปวดถ้าจำเป็น",
      ],
      en: [
        "Wait 1 hour before chewing on the filled side",
        "Chew on the opposite side on the first day",
        "Brush and floss as usual",
        "Take pain relief if needed",
      ],
    },
    donts: {
      th: [
        "เคี้ยวอาหารแข็งหรือเหนียวบริเวณที่อุด",
        "อาหารเย็นจัดหรือร้อนจัดในวันแรก",
        "กัดเล็บหรือของแข็ง",
      ],
      en: [
        "Chewing hard or sticky food on the filled area",
        "Very cold or very hot food on the first day",
        "Biting your nails or hard objects",
      ],
    },
    notes: {
      th: [
        "อาการเสียวฟันชั่วคราวถือเป็นปกติ",
        "หากกัดแล้วรู้สึกสูงผิดปกติ ให้ติดต่อคลินิกเพื่อปรับ",
        "ชาที่ริมฝีปากจะหายใน 2–3 ชม.",
      ],
      en: [
        "Temporary tooth sensitivity is normal",
        "If the filling feels too high when you bite, contact the clinic to adjust it",
        "Numbness in your lips will wear off in 2–3 hours",
      ],
    },
  },
  {
    key: "rootcanal",
    label: { th: "รักษารากฟัน", en: "Root Canal Treatment" },
    dos: {
      th: [
        "ทานยาปฏิชีวนะและยาแก้ปวดตามสั่ง",
        "เคี้ยวด้านตรงข้ามจนกว่าจะครอบฟัน",
        "รักษาความสะอาดช่องปากตามปกติ",
        "มาตามนัดเพื่อทำครอบฟัน",
      ],
      en: [
        "Take antibiotics and pain medication as prescribed",
        "Chew on the opposite side until the crown is placed",
        "Keep up your usual oral hygiene",
        "Return as scheduled to have the crown placed",
      ],
    },
    donts: {
      th: [
        "เคี้ยวฟันซี่ที่รักษาก่อนครอบฟัน",
        "อาหารแข็งหรือเหนียว",
        "ละเลยอาการปวดบวมที่เพิ่มขึ้น",
      ],
      en: [
        "Chewing with the treated tooth before it is crowned",
        "Hard or sticky food",
        "Ignoring increasing pain or swelling",
      ],
    },
    notes: {
      th: [
        "อาการระบมเล็กน้อย 2–3 วันถือเป็นปกติ",
        "ฟันที่รักษารากควรครอบฟันเพื่อความแข็งแรง",
        "หากปวดมากหรือบวม ให้ติดต่อคลินิกทันที",
      ],
      en: [
        "Mild tenderness for 2–3 days is normal",
        "A root-treated tooth should be crowned for strength",
        "If you have severe pain or swelling, contact the clinic immediately",
      ],
    },
  },
  {
    key: "fluoride",
    label: { th: "เคลือบฟลูออไรด์", en: "Fluoride Treatment" },
    dos: {
      th: [
        "งดน้ำและอาหารอย่างน้อย 30 นาที",
        "ทานอาหารอ่อนและไม่ร้อนจัดในมื้อแรก",
        "แปรงฟันและใช้ไหมขัดฟันตามปกติในวันถัดไป",
        "ดื่มน้ำเปล่าได้หลังครบ 30 นาที",
      ],
      en: [
        "Avoid food and drink for at least 30 minutes",
        "Eat soft, not-too-hot food at your first meal",
        "Brush and floss as usual the next day",
        "You may drink water after the 30 minutes are up",
      ],
    },
    donts: {
      th: [
        "อาหารและเครื่องดื่มร้อนจัดใน 30 นาทีแรก",
        "อาหารแข็ง กรอบ หรือเหนียวในวันแรก",
        "แปรงฟันแรงหรือบ้วนปากแรงทันทีหลังเคลือบ",
        "ใช้น้ำยาบ้วนปากที่มีแอลกอฮอล์ในวันแรก",
      ],
      en: [
        "Very hot food and drinks within the first 30 minutes",
        "Hard, crunchy, or sticky food on the first day",
        "Brushing or rinsing vigorously right after the treatment",
        "Using alcohol-based mouthwash on the first day",
      ],
    },
    notes: {
      th: [
        "ฟลูออไรด์ช่วยเสริมความแข็งแรงของเคลือบฟันและลดฟันผุ",
        "ฟลูออไรด์วานิชอาจทำให้ฟันดูเหลืองชั่วคราว และจะหลุดเมื่อแปรงฟันวันถัดไป",
        "ควรเคลือบฟลูออไรด์ต่อเนื่องทุก 6 เดือน",
      ],
      en: [
        "Fluoride strengthens tooth enamel and reduces decay",
        "Fluoride varnish may make teeth look slightly yellow for a while and comes off when you brush the next day",
        "Fluoride treatment should be repeated every 6 months",
      ],
    },
  },
  {
    key: "whitening",
    label: { th: "ฟอกสีฟัน", en: "Teeth Whitening" },
    dos: {
      th: [
        "ดื่มน้ำเปล่าและแปรงฟันเบาๆ ตามปกติ",
        "ใช้ยาสีฟันสำหรับฟันเสียวหากรู้สึกเสียวฟัน",
        "ทานอาหารสีอ่อน เช่น ข้าว นม ไก่ ใน 48 ชม.แรก",
      ],
      en: [
        "Drink water and brush gently as usual",
        "Use toothpaste for sensitive teeth if you feel sensitivity",
        "Eat light-colored foods such as rice, milk, and chicken for the first 48 hours",
      ],
    },
    donts: {
      th: [
        "ชา กาแฟ ไวน์แดง น้ำอัดลมสีเข้ม ใน 48 ชม.",
        "อาหารสีจัด เช่น แกงกะหรี่ ซอสถั่วเหลือง ซีอิ๊ว",
        "สูบบุหรี่",
        "อาหารและเครื่องดื่มร้อนจัดหรือเย็นจัด",
      ],
      en: [
        "Tea, coffee, red wine, and dark soft drinks for 48 hours",
        "Strongly colored foods such as curry and soy sauce",
        "Smoking",
        "Very hot or very cold food and drinks",
      ],
    },
    notes: {
      th: [
        "อาการเสียวฟันชั่วคราว 1–2 วันถือเป็นปกติ",
        "หลีกเลี่ยงอาหาร/เครื่องดื่มสีเข้มเพื่อคงผลฟอกให้นาน",
        "ฟอกสีฟันซ้ำได้ตามคำแนะนำของทันตแพทย์",
      ],
      en: [
        "Temporary sensitivity for 1–2 days is normal",
        "Avoid dark-colored food and drinks to make the results last longer",
        "Whitening can be repeated as advised by your dentist",
      ],
    },
  },
  {
    key: "veneer",
    label: { th: "วีเนียร์", en: "Veneers" },
    dos: {
      th: [
        "แปรงฟันและใช้ไหมขัดฟันอย่างนุ่มนวลตามปกติ",
        "ใส่ฟันยางกันกัดหากมีนิสัยนอนกัดฟัน",
        "เคี้ยวอาหารด้วยฟันกรามแทนการกัดด้วยฟันหน้า",
      ],
      en: [
        "Brush and floss gently as usual",
        "Wear a night guard if you tend to grind your teeth",
        "Chew with your back teeth instead of biting with your front teeth",
      ],
    },
    donts: {
      th: [
        "กัดของแข็ง เช่น น้ำแข็ง ปากกา เปลือกถั่ว",
        "ใช้ฟันหน้าฉีกหรือกัดของเหนียว",
        "อาหารและเครื่องดื่มสีเข้มมากเกินไป",
      ],
      en: [
        "Biting hard objects such as ice, pens, or nut shells",
        "Using your front teeth to tear or bite chewy food",
        "Excessively dark-colored food and drinks",
      ],
    },
    notes: {
      th: [
        "อาจเสียวฟันเล็กน้อยในช่วงแรกและดีขึ้นเอง",
        "หลีกเลี่ยงแรงกระแทกบริเวณฟันหน้า",
        "พบทันตแพทย์ตามนัดเพื่อตรวจขอบวีเนียร์",
      ],
      en: [
        "Mild sensitivity at first is normal and improves on its own",
        "Avoid impact to your front teeth",
        "See your dentist as scheduled to check the edges of the veneers",
      ],
    },
  },
  {
    key: "provisional",
    label: { th: "ครอบฟัน / วีเนียร์ชั่วคราว", en: "Temporary Crown / Veneer" },
    dos: {
      th: [
        "ดูแลชิ้นงานชั่วคราวอย่างระมัดระวัง",
        "แปรงฟันเบาๆ และใช้ไหมขัดฟันโดยดึงออกด้านข้าง",
        "เคี้ยวอาหารด้านตรงข้ามจนกว่าจะใส่ชิ้นงานถาวร",
      ],
      en: [
        "Take care of the temporary restoration carefully",
        "Brush gently and floss by pulling the floss out to the side",
        "Chew on the opposite side until the permanent restoration is placed",
      ],
    },
    donts: {
      th: [
        "อาหารเหนียวหรือแข็ง เช่น หมากฝรั่ง ลูกอม ของกรอบ",
        "ดึงไหมขัดฟันขึ้นตรงๆ (อาจทำให้ชิ้นงานหลุด)",
        "ใช้ฟันด้านที่ทำกัดของแข็ง",
      ],
      en: [
        "Sticky or hard food such as gum, candy, and crunchy snacks",
        "Pulling floss straight up (it may dislodge the restoration)",
        "Biting hard objects with the treated side",
      ],
    },
    notes: {
      th: [
        "ชิ้นงานชั่วคราวอาจเสียวฟันหรือสีไม่เท่าชิ้นงานจริง",
        "หากชิ้นงานชั่วคราวหลุด ให้เก็บไว้และติดต่อคลินิก",
        "มาใส่ครอบฟัน/วีเนียร์ถาวรตามนัด",
      ],
      en: [
        "A temporary restoration may feel sensitive or not match the color of the final one",
        "If the temporary restoration comes off, keep it and contact the clinic",
        "Return as scheduled to have the permanent crown/veneer placed",
      ],
    },
  },
];

// Lookup a single treatment by key. Returns the entry or null when unknown.
export function getPostOp(key) {
  return POSTOP_TREATMENTS.find((t) => t.key === key) || null;
}
